/* eslint-disable max-len */
import { type Dispatch, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type EventApplicationEntrypointContextDataFragment } from '../../../../api/fragments';
import { type EventCustomizedFieldInput } from '../../../../api/types';
import { useUploadOcrFiles } from '../../../../components/ocr';
import OcrFilesManager from '../../../../components/ocr/OcrFilesManager';
import { useThemeComponents } from '../../../../themes';
import { getInitialValues } from '../../../../utilities/kycPresets';
import useHandleError from '../../../../utilities/useHandleError';
import useAgreementSubmission from '../../../shared/CIPage/ConsentAndDeclarations/useAgreementSubmission';
import { getAgreementValues } from '../../../shared/CIPage/ConsentAndDeclarations/useAgreementsValues';
import useDeleteApplicationDocument from '../../../shared/useDeleteApplicationDocument';
import useUploadApplicationDocument from '../../../shared/useUploadApplicationDocument';
import useAppointmentSubmission from '../../StandardApplicationEntrypoint/AppointmentPage/useAppointmentSubmission';
import useCustomerDetailsSubmission from '../../StandardApplicationEntrypoint/CustomerDetailsPage/useCustomerDetailsSubmission';
import useUpdateApplicationFields from '../../StandardApplicationEntrypoint/CustomerDetailsPage/useUpdateApplicationFields';
import { type Action, type State } from '../../StandardApplicationEntrypoint/Journey/reducer';
import { getApplicantKyc } from '../../StandardApplicationEntrypoint/KYCPage/getKyc';
import ApplicantForm from '../ApplicantForm';
import { getMyInfoTemporaryValues, removeMyInfoTemporaryValues } from '../ApplicantForm/CustomerDetails/utils';
import { type ApplicantFormValues } from '../ApplicantForm/shared';
import useUpdateEventApplication from '../ApplicantForm/useUpdateEventApplication';
import {
    EventJourneyKycAndAgreementProvider,
    useEventJourneyKycAndAgreementContext,
} from '../Entrypoint/EventJourneyKycAndAgreement';
import { EventJourneySetupProvider, useEventJourneySetupContext } from '../Entrypoint/EventJourneySetup';
import { type EventApplicationState } from '../Journey/JourneyController';

export type EventApplicationProps = {
    endpoint: EventApplicationEntrypointContextDataFragment;
    state: State<EventApplicationState>;
    dispatch: Dispatch<Action<EventApplicationState>>;
};

/**
 * This page use mostly the same logic with the Entrypoint
 * But, it used for redirection from MyInfo, which is the KYC page, which is the initial page like Entrypoint
 */
const Inner = ({ state, dispatch }: Omit<EventApplicationProps, 'endpoint'>) => {
    const { t } = useTranslation(['eventApplicantForm', 'common']);
    const { notification } = useThemeComponents();

    const { token, application } = state;

    const { eventModule, endpoint } = useEventJourneySetupContext();
    const { customerKind, prefill } = useEventJourneyKycAndAgreementContext();

    const updateEvent = useUpdateEventApplication(endpoint);
    const submitAgreements = useAgreementSubmission();
    const submitCustomerDetails = useCustomerDetailsSubmission();
    const updateApplicationFields = useUpdateApplicationFields();
    const submitAppointment = useAppointmentSubmission();

    const uploadOcrFiles = useUploadOcrFiles();
    const uploadDocument = useUploadApplicationDocument(token);
    const removeDocument = useDeleteApplicationDocument(token);

    const onSubmit = useHandleError(
        async ({ appointment, ...values }: ApplicantFormValues) => {
            notification.loading({
                content: t('eventApplicantForm:messages.creationSubmitting'),
                duration: 0,
                key: 'primary',
            });

            const updateEventResult = await updateEvent(token, values, customerKind);
            const submitAgreementResult = await submitAgreements(
                updateEventResult.token,
                values.agreements,
                customerKind,
                values.hasGuarantor
            );

            const showRemarks = application?.bank?.showCommentsField;

            if (showRemarks) {
                // we wait to update remarks first
                await updateApplicationFields(submitAgreementResult.token, values.remarks);
                // then we later call the KYC
                // as calling submit customer will immediately call the next step and will have not the remarks
            }
            const submitCustomerDetailsResult = await submitCustomerDetails(
                submitAgreementResult.token,
                values.customer.fields,
                customerKind,
                prefill
            );
            await uploadOcrFiles(submitCustomerDetailsResult.token);

            notification.destroy('primary');

            const finalResult =
                values.configuration.testDrive &&
                eventModule?.appointmentModule &&
                eventModule?.appointmentModule?.__typename === 'AppointmentModule' &&
                eventModule?.displayAppointmentDatepicker
                    ? await submitAppointment(
                          submitCustomerDetailsResult.token,
                          appointment,
                          eventModule.appointmentModule,
                          eventModule.displayAppointmentDatepicker,
                          eventModule.company.timeZone
                      )
                    : submitCustomerDetailsResult;

            if (finalResult.__typename === 'GiftVoucherJourneyContext') {
                throw new Error('unsupported journey context');
            }
            const { application: newApplication } = finalResult;

            if (newApplication.__typename !== 'EventApplication') {
                throw new Error('unexpected type');
            }

            // Remove temporary values
            removeMyInfoTemporaryValues(application.id);

            dispatch({
                type: 'next',
                token: finalResult.token,
                application: newApplication,
            });
        },
        [
            notification,
            t,
            updateEvent,
            token,
            customerKind,
            submitAgreements,
            eventModule.appointmentModule,
            eventModule.displayAppointmentDatepicker,
            eventModule.company.timeZone,
            submitCustomerDetails,
            prefill,
            uploadOcrFiles,
            submitAppointment,
            application.id,
            dispatch,
            updateApplicationFields,
            application?.bank?.showCommentsField,
        ]
    );

    const initialValues: ApplicantFormValues = useMemo(() => {
        const { vehicle } = application;

        if (vehicle.__typename !== 'LocalVariant') {
            throw new Error('Invalid Variant');
        }

        const myInfoTemporaryValues = getMyInfoTemporaryValues(application.id);

        const applicantKYC = getApplicantKyc(application.applicantKYC);
        const initialCustomerFields = application.applicant ? application.applicant.fields : [];

        return {
            customer: applicantKYC ? { fields: getInitialValues(initialCustomerFields, applicantKYC) } : null,
            agreements: getAgreementValues(application.applicantAgreements),
            configuration: application.configuration,
            vehicleInterest: {
                model: vehicle.model.parentModel ? vehicle.model.parentModelId : vehicle.model.id,
                subModel: vehicle.submodelId,
                variant: vehicle.id,
            },
            isCorporateCustomer: false,
            tradeInVehicle: application.tradeInVehicle,
            hasGuarantor: false,
            dealerId: application.dealerId,
            prefill: false,
            customizedFields: application.customizedFields as EventCustomizedFieldInput[],
            appointment: {
                date: myInfoTemporaryValues?.appointment?.date,
                time: myInfoTemporaryValues?.appointment?.time,
                useCurrentDateTime: myInfoTemporaryValues?.appointment?.useCurrentDateTime ?? false,
            },
            remarks: application.remarks ?? '',
        };
    }, [application]);

    return (
        <ApplicantForm
            initialValues={initialValues}
            onSubmit={onSubmit}
            removeDocument={removeDocument}
            state={state}
            uploadDocument={uploadDocument}
        />
    );
};

const EventApplication = ({ endpoint, state, dispatch }: EventApplicationProps) => (
    <OcrFilesManager>
        <EventJourneySetupProvider application={state.application} endpoint={endpoint} event={state.application.event}>
            <EventJourneyKycAndAgreementProvider>
                <Inner dispatch={dispatch} state={state} />
            </EventJourneyKycAndAgreementProvider>
        </EventJourneySetupProvider>
    </OcrFilesManager>
);

export default EventApplication;
